import React, { FunctionComponent, useEffect, useState } from 'react';
import withStyles from 'react-jss';
import { Container, Grid } from 'semantic-ui-react';

import Table from 'components/Table';
import {
  Direction,
  SortDefaultColumn,
  TableHeader,
} from 'components/Table/Table';
import { ChartPlaceholder } from 'components/Placeholder';
import FormError from 'components/FormComponents/FormError';

import { ClassProp } from 'helpers/types';
import { formatDate } from 'helpers/date';
import { CampaignSummary } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantCampaigns/MerchantCampaigns.helpers';
import {
  Budget,
  BudgetType,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';

import styles from './CampaignTable.styles';
import Checkbox from './Checkbox';
import { useFormContext } from 'react-hook-form';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  campaigns: CampaignSummary[];
  loading?: boolean;
  onCampaignSelectedHandler: (selectedCampaign: string) => void;
  selectedCampaign?: string;
  name: string;
};

const sortDefaultColumn: SortDefaultColumn = {
  name: 'Start Date',
  sortable: ['campaign', 'startDate'],
  direction: Direction.DESCENDING,
};

const secondarySortColumn: SortDefaultColumn = {
  name: 'End Date',
  sortable: ['campaign', 'endDate'],
  direction: Direction.DESCENDING,
};

export const transformBudgetType = (budgetType: BudgetType): string => {
  switch (budgetType) {
    case BudgetType.FIXED_AMOUNT:
      return 'Fixed';
    case BudgetType.FIXED_AMOUNT_FIXED_DATE_RANGE:
      return 'Monthly';
    case BudgetType.OPEN:
      return 'Open';
    default:
      return 'N/A';
  }
};

const renderTable = (
  campaigns: CampaignSummary[],
  classes: Styles,
  tableHeader: (
    onSelectedCampaign: (selectedCampaign: string) => any,
  ) => TableHeader[],
  onCampaignSelectedHandler: (selectedCampaign: string) => void,
  selectedCampaign?: string,
) => {
  if (campaigns.length === 0) {
    return (
      <div>
        <h2 className={classes.header}>
          No Campaigns were found for this Merchant
        </h2>
      </div>
    );
  }
  return (
    <Grid container>
      <Table
        tableHeader={tableHeader(onCampaignSelectedHandler)}
        data={campaigns}
        sortDefaultColumn={sortDefaultColumn}
        sortSecondaryColumn={secondarySortColumn}
        itemsKey="campaign.uuid"
        selectedDataIdRow={selectedCampaign}
        splitEveryArg={10}
      />
    </Grid>
  );
};

const CampaignTable: FunctionComponent<Props> = ({
  classes,
  campaigns: merchantCampaigns,
  onCampaignSelectedHandler,
  selectedCampaign,
  loading,
  name,
}) => {
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  useEffect(() => {
    const filteredCampaigns = merchantCampaigns.map((merchantCampaign) => {
      const { startDate, endDate } = merchantCampaign.campaign;
      // We remove the time information from the dates,
      // so that we can sort properly by campaign Start Date and then by End Date
      merchantCampaign.campaign.startDate = startDate.split('T')[0];
      merchantCampaign.campaign.endDate = endDate.split('T')[0];
      return merchantCampaign;
    });
    setCampaigns(filteredCampaigns);
  }, [merchantCampaigns]);

  const { errors } = useFormContext();
  const fieldErrors = errors[name];

  const tableHeader = (
    onSelectedCampaign: (selectedCampaign: string) => any,
  ): TableHeader[] => [
    {
      label: '',
      source: ['campaign', 'uuid'],
      component: {
        Element: Checkbox,
        props: {
          onClickHandler: onSelectedCampaign,
          selectedCampaign,
          type: 'radio',
        },
      },
    },
    {
      source: ['campaign', 'startDate'],
      sortable: ['campaign', 'startDate'],
      label: 'Start Date',
      transform: (date: string) => formatDate(date, 'MMM DD, YYYY'),
      options: {
        class: 'dateLabel',
      },
    },
    {
      source: ['campaign', 'endDate'],
      sortable: ['campaign', 'endDate'],
      label: 'End Date',
      transform: (date: string) => formatDate(date, 'MMM DD, YYYY'),
      options: {
        class: 'dateLabel',
      },
    },
    {
      source: ['campaign', 'name'],
      label: 'Campaign Name',
    },
    {
      source: ['budgets', '0', 'type'],
      label: 'Budget Type',
      transform: transformBudgetType,
    },
    {
      source: ['budgets'],
      label: 'Months',
      transform: (budgets: Budget[]) => {
        if (
          budgets.length > 0 &&
          budgets[0].type === BudgetType.FIXED_AMOUNT_FIXED_DATE_RANGE
        ) {
          return budgets.length;
        }
        return 'N/A';
      },
      justify: 'left',
    },
    {
      source: ['advertiser', 'name'],
      label: 'Advertiser',
    },
    {
      source: ['budgets', '0', 'totalBudget'],
      label: 'Budget Amt',
      transform: (totalBudget: number) => `$${totalBudget}`,
      justify: 'right',
    },
    {
      source: ['budgets', '0', 'balance'],
      label: 'Budget Balance',
      transform: (balance: number) => `$${balance}`,
      justify: 'right',
    },
  ];

  return (
    <Container className={classes.container}>
      {loading ? (
        <ChartPlaceholder />
      ) : (
        renderTable(
          campaigns,
          classes,
          tableHeader,
          onCampaignSelectedHandler,
          selectedCampaign,
        )
      )}
      {fieldErrors && (
        <div className={classes.formError}>
          <FormError name={name} errors={errors} />
        </div>
      )}
    </Container>
  );
};

export default withStyles(styles)(CampaignTable);
